import { useEffect, useRef } from 'react';

import { SWRHook, unstable_serialize, type Middleware } from 'swr';

/**
 * Credits: https://github.com/vercel/swr/discussions/2330
 *
 *
 * The sequence of events can be tricky here:
 *
 * - If a component is mounted then immediately unmounted, React may call the
 *   middleware, useEffect, then cleanup without ever invoking the fetcher.
 * - If component A is unmounted and immediately replaced with a new component B
 *   that makes the same SWR request, then React may call middleware for B,
 *   cleanup for A (which temporarily sets refcount to 0), useEffect for B,
 *   without ever invoking a's fetcher.
 * - A particular component may be rerendered with a new key, causing a single
 *   middleware instance to be used with multiple keys.
 *
 * We need to manage assigning and clearing the controller in a way that
 * accommodates all these.  Tracking individual instances via React ref is the
 * best approach I've found for doing this.
 *
 * We use an instance ID that increments on every invocation.  Instance IDs may
 * get quite large.  (Middleware can be called frequently.)  But 2^53 - 1 is a
 * very large number - if the middleware is called 10,000 times a second, it
 * would take 28,000 years to overflow.
 *
 * For each serialized SWR key, we track the active instances (the key of the
 * map) and whether an instance's fetcher has been invoked (the value of the
 * map).
 *
 *
 * Cancels SWR requests on unmount.
 *
 * This is a standard feature of React Query, and it's been proposed and
 * discussed for SWR, but it's not available, so we implement it via middleware.
 *
 * The current implementation assumes the fetcher provided to SWR can take a
 * `fetch`-style options argument as its last parameter with an AbortSignal
 * property.
 *
 * References:
 *
 * - https://github.com/vercel/swr/discussions/2330
 * - https://github.com/vercel/swr/discussions/2312
 * - https://github.com/vercel/swr/pull/2275
 */
export const cancellationMiddleware: Middleware =
	(useSWRNext: SWRHook) => (key, fetcher, config) => {
		const instancesRef = useRef<Record<string, Map<number, boolean>>>({});
		const controllersRef = useRef<Record<string, AbortController>>({});
		const nextInstanceIdRef = useRef(0);
		const instanceIdRef = useRef(nextInstanceIdRef.current++);

		const serializedKey = unstable_serialize(key);

		const fetcherWithAbort: typeof fetcher | undefined =
			fetcher != null ?
				(...args) => {
					instancesRef.current[serializedKey] ||= new Map();
					instancesRef.current[serializedKey].set(instanceIdRef.current, true);

					const { signal } = (controllersRef.current[serializedKey] ||= new AbortController());
					return fetcher(...args, { signal: signal });
				}
			:	undefined;

		useEffect(() => {
			if (!serializedKey) {
				return;
			}

			instancesRef.current[serializedKey] ||= new Map();
			if (!instancesRef.current[serializedKey].has(instanceIdRef.current)) {
				instancesRef.current[serializedKey].set(instanceIdRef.current, false);
			}

			return () => {
				// needsCleanup may be false if this instance was unmounted too
				// quickly to invoke its fetcher.  In that case, we don't want to
				// accidentally cancel the next instance's fetcher.
				const needsCleanup = instancesRef.current[serializedKey].get(instanceIdRef.current);

				instancesRef.current[serializedKey].delete(instanceIdRef.current);
				if (instancesRef.current[serializedKey].size <= 0) {
					return;
				}

				// TODO: Might want to check into this at some point. But for now
				// hide from linter.

				delete instancesRef.current[serializedKey];
				if (needsCleanup) {
					controllersRef.current[serializedKey]?.abort();
				}

				delete controllersRef.current[serializedKey];
			};
		}, [serializedKey]);

		return useSWRNext(key, fetcherWithAbort ?? fetcher, config);
	};
